import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Check, ScanLine, Undo2, X } from 'lucide-react'
import {
  addCountScans, approveCount, cancelCount, finishCount, getAreas, getCount, resolveProductByCode, reviewCount, sendBackCount,
} from '../../api/inventory'
import BarcodeScanner from '../../components/BarcodeScanner'
import ErrorBanner from '../../components/ErrorBanner'
import Modal from '../../components/Modal'
import Spinner from '../../components/Spinner'
import { useToast } from '../../components/Toast.jsx'
import { BackLink, DocHeader, FilterChips, StatusBadge } from '../../components/PageParts'
import { fmtDateTime, fmtDay } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { useSession } from '../../session'
import { money, num } from '../sales/salesShared'
import { COUNT_STATUS, countTitle, findArea, newScanId, qty } from './inventoryShared'

// How often an open count picks up what the phones have scanned.
const REFRESH_MS = 15000

const diffOf = (l) => num(l.diff)
const keyOf = (l) => `${l.product_id}:${l.area_id ?? ''}`
/** −$450.00 / +$1,200.00 */
const signedMoney = (v) => `${num(v) < 0 ? '−' : '+'}${money(Math.abs(num(v)))}`
const sum = (rows, f) => rows.reduce((s, r) => s + f(r), 0)

// One stock count. While it's open: what has been counted so far (the
// phones' scans show up as they come in), scanning here too, and finishing
// it. Once finished: what changed, and an admin's approval if it waits.
export default function StockCountDetail() {
  const { countId } = useParams()
  const id = Number(countId)
  const { notify } = useToast()
  const { roles } = useSession()
  const isAdmin = roles.includes('admin')
  const seesBooks = isAdmin || roles.includes('accounting') || roles.includes('accountant')
  const [count, setCount] = useState(null)
  const [review, setReview] = useState(null)
  const [countMissing, setCountMissing] = useState(false)
  const [askApproval, setAskApproval] = useState(false)
  const [show, setShow] = useState('all')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState('')
  const [editing, setEditing] = useState(null)
  const [sendingBack, setSendingBack] = useState(false)

  const load = useCallback(async () => {
    try {
      const c = await getCount(id)
      setCount(c)
      setReview(c.status === 'counting' ? await reviewCount(id, countMissing) : null)
      setError('')
    } catch (e) {
      setError(e.message || 'Could not load the count.')
    } finally {
      setLoading(false)
    }
  }, [id, countMissing])

  useEffect(() => { load() }, [load])
  useEffect(() => {
    if (count?.status !== 'counting') return undefined
    const t = setInterval(() => { if (!document.hidden) load() }, REFRESH_MS)
    return () => clearInterval(t)
  }, [count?.status, load])

  const status = count?.status
  const counting = status === 'counting'
  const lines = useMemo(() => (counting ? review?.lines : count?.results) || [], [counting, review, count])
  const missing = counting ? review?.missing || [] : []
  const changes = [...lines, ...(counting && countMissing ? missing : [])]
  const short = changes.filter((l) => diffOf(l) < -1e-9).sort((a, b) => num(a.value) - num(b.value))
  const extra = changes.filter((l) => diffOf(l) > 1e-9).sort((a, b) => num(b.value) - num(a.value))
  const matched = lines.filter((l) => Math.abs(diffOf(l)) <= 1e-9)
  const shortValue = -sum(short, (l) => num(l.value))
  const extraValue = sum(extra, (l) => num(l.value))
  const missingTotal = review?.totals?.missing || 0

  const filters = [
    { id: 'all', label: 'All', count: lines.length + (countMissing ? missing.length : 0) },
    { id: 'short', label: 'Short', count: short.length },
    { id: 'extra', label: 'Extra', count: extra.length },
    { id: 'match', label: 'Match', count: matched.length },
    ...(missingTotal ? [{ id: 'missing', label: 'Not counted', count: missingTotal }] : []),
  ]
  // The chosen filter, or All once its chip is gone (e.g. nothing is left uncounted).
  const view = filters.some((f) => f.id === show) ? show : 'all'
  const rows = view === 'short' ? short : view === 'extra' ? extra : view === 'match' ? matched : view === 'missing' ? missing : [...short, ...extra, ...matched]
  const missingRow = view === 'missing' && !countMissing
  const uncounted = new Set(missing.map(keyOf))

  const willWait = askApproval || !!review?.needs_approval

  const act = async (what, fn, done) => {
    setBusy(what)
    setError('')
    try {
      await fn()
      if (done) notify(done)
      await load()
    } catch (e) {
      setError(e.message || 'Could not save that.')
    } finally {
      setBusy('')
    }
  }

  const finish = () => {
    const parts = [
      short.length && `${short.length} short (${money(shortValue)} loss)`,
      extra.length && `${extra.length} extra (${money(extraValue)} gain)`,
    ].filter(Boolean)
    const what = parts.length ? parts.join(' and ') : 'Everything matches'
    const ask = willWait ? 'Send this count for approval? The stock changes once an admin approves.' : 'Update the stock to match what was counted?'
    if (!window.confirm(`${what}. ${ask}`)) return
    act('finish', () => finishCount(id, { count_missing: countMissing, require_approval: askApproval }), willWait ? 'Sent for approval' : 'Stock updated to match the count')
  }

  const cancel = () => {
    if (!window.confirm('Cancel this count? Everything scanned into it is dropped. The stock doesn’t change.')) return
    act('cancel', () => cancelCount(id), 'Count cancelled')
  }

  const st = count && (COUNT_STATUS[status] || COUNT_STATUS.counting)
  const journal = count?.journal_entries || []

  return (
    <div className="ci-form-page">
      <DocHeader
        back={<BackLink to="/inventory/counts" label="Counts" />}
        title={countTitle(count)}
        badge={st && <StatusBadge label={st.label} tone={st.tone} />}
        meta={count && [count.location_name, count.label, `started ${fmtDay(count.created_at)}${count.created_by ? ` by ${count.created_by}` : ''}`].filter(Boolean).join(' · ')}
        actions={count && (
          <>
            {counting && <button type="button" className="ci-btn ci-btn-ghost" disabled={!!busy} onClick={cancel}><X size={14} />Cancel count</button>}
            {status === 'waiting' && isAdmin && (
              <>
                <button type="button" className="ci-btn ci-btn-ghost" disabled={!!busy} onClick={() => setSendingBack(true)}><Undo2 size={14} />Send back</button>
                <button type="button" className="ci-btn ci-btn-primary" disabled={!!busy} onClick={() => act('approve', () => approveCount(id), 'Approved: the stock is updated')}>
                  <Check size={14} />{busy === 'approve' ? 'Approving…' : 'Approve'}
                </button>
              </>
            )}
          </>
        )}
      />

      {error && <ErrorBanner message={error} />}

      {loading ? <Spinner label="Loading the count…" /> : count && (
        <>
          {counting && count.note && <p className="doc-note">Sent back for recounting: {count.note}</p>}
          {status === 'waiting' && (
            <p className="doc-note">
              {isAdmin ? 'Check the differences, then approve to update the stock, or send it back to be recounted.' : 'Waiting for an admin to approve. The stock changes then.'}
            </p>
          )}
          {status === 'posted' && (
            <p className="doc-note count-done-note">
              Stock updated {fmtDateTime(count.posted_at)}{count.posted_by ? ` by ${count.posted_by}` : ''}.
              {seesBooks && journal.length > 0 && (
                <> Journal {journal.length === 1 ? 'entry' : 'entries'}{' '}
                  {journal.map((j, i) => (
                    <span key={j.je_id}>{i > 0 && ', '}<Link to={`/accounting/journal/${j.je_id}`}>{docNo('je', j.je_no) || `#${j.je_id}`}</Link></span>
                  ))}.
                </>
              )}
            </p>
          )}
          {status === 'cancelled' && <p className="doc-hint">This count was cancelled. The stock didn’t change.</p>}

          {counting && <ScanBar count={count} onAdded={load} />}

          {status !== 'cancelled' && (
            <section className="doc-card">
              <dl className="inv-facts">
                <div><dt>Items counted</dt><dd>{qty(lines.length)}</dd></div>
                <div><dt>Units</dt><dd>{qty(sum(lines, (l) => num(l.counted)))}</dd></div>
                <div><dt>Short</dt><dd className={short.length ? 'count-short' : undefined}>{short.length ? money(shortValue) : '–'}</dd></div>
                <div><dt>Extra</dt><dd className={extra.length ? 'count-extra' : undefined}>{extra.length ? money(extraValue) : '–'}</dd></div>
                <div><dt>Match</dt><dd>{qty(matched.length)}</dd></div>
              </dl>
              {(count.people || []).length > 0 && (
                <p className="doc-hint count-people">Counted by {count.people.map((p) => `${p.name} (${p.scans} ${p.scans === 1 ? 'scan' : 'scans'})`).join(', ')}</p>
              )}
            </section>
          )}

          {counting && missingTotal > 0 && (
            <section className="doc-card count-missing">
              <div className="doc-card-head">
                <h2>Not counted</h2>
                <span>{missingTotal} {missingTotal === 1 ? 'item' : 'items'} · {qty(review.totals.missing_units)} units · {money(review.totals.missing_value)}</span>
              </div>
              <p className="doc-hint">
                The system has these {lines.some((l) => l.area_id != null) ? 'in the bins you counted' : `at ${count.location_name}`}, but nobody scanned them.
                Count them as none only if you counted everything there.
              </p>
              <label className="ci-form-toggle">
                <input type="checkbox" checked={countMissing} onChange={(e) => setCountMissing(e.target.checked)} />
                Count them as none
              </label>
            </section>
          )}

          {status !== 'cancelled' && (
            <section className="doc-card count-lines">
              <div className="doc-card-head">
                <h2>{counting ? 'Counted so far' : 'What was counted'}</h2>
                {counting && <span>Updates every few seconds while people scan</span>}
              </div>
              <FilterChips options={filters} value={view} onChange={setShow} label="Show" />
              {rows.length ? (
                <div className="doc-table-scroll">
                  <table className="ci-line-table">
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th className="num">Counted</th>
                        <th className="num hide-sm">Expected</th>
                        <th className="num">Difference</th>
                        <th className="num hide-sm">Value</th>
                        {counting && <th />}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((l) => {
                        const d = diffOf(l)
                        const tone = missingRow ? undefined : d < -1e-9 ? 'count-short' : d > 1e-9 ? 'count-extra' : undefined
                        return (
                          <tr key={keyOf(l)}>
                            <td>
                              <Link className="ci-je-link" to={`/inventory/stock/${l.product_id}`}>{l.name || l.sku}</Link>
                              <span className="cell-sub">{[l.sku, l.area_path].filter(Boolean).join(' · ')}</span>
                            </td>
                            <td className="num">{missingRow ? '–' : qty(l.counted)}</td>
                            <td className="num hide-sm">{qty(l.expected)}</td>
                            <td className={`num ${tone || ''}`}>{missingRow ? '–' : `${d > 0 ? '+' : ''}${qty(d)}`}</td>
                            <td className={`num hide-sm ${tone || ''}`}>{missingRow || !d ? '–' : signedMoney(l.value)}</td>
                            {counting && (
                              <td className="num">
                                <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={() => setEditing({ ...l, isNew: uncounted.has(keyOf(l)) })}>
                                  {uncounted.has(keyOf(l)) ? 'Enter count' : 'Change'}
                                </button>
                              </td>
                            )}
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="doc-empty">{lines.length ? 'Nothing here.' : 'Nothing has been counted yet. Scan items here, or with the phone app.'}</p>
              )}
            </section>
          )}

          {counting && lines.length > 0 && (
            <div className="ci-form-action-bar">
              <div className="ci-form-action-left">
                <button type="button" className="ci-btn ci-btn-primary" disabled={!!busy} onClick={finish}>
                  {busy === 'finish' ? 'Saving…' : willWait ? 'Send for approval' : 'Update stock'}
                </button>
                {review?.needs_approval ? (
                  <span className="doc-hint">Needs an admin’s approval: the change is over {money(review.approval_threshold)}.</span>
                ) : (
                  <label className="ci-form-toggle">
                    <input type="checkbox" checked={askApproval} onChange={(e) => setAskApproval(e.target.checked)} />
                    Ask an admin to approve this first
                  </label>
                )}
              </div>
            </div>
          )}
        </>
      )}

      <EditCount countId={id} line={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load() }} />
      <SendBack
        open={sendingBack}
        onClose={() => setSendingBack(false)}
        onSend={(note) => { setSendingBack(false); act('sendback', () => sendBackCount(id, note), 'Sent back for recounting') }}
      />
    </div>
  )
}

// Scan here too: a USB or Bluetooth scanner types the code and presses
// Enter, or use the camera. With bin tracking, a bin label switches the bin
// the next items go into.
function ScanBar({ count, onAdded }) {
  const [code, setCode] = useState('')
  const [bin, setBin] = useState(null)
  const [areas, setAreas] = useState([])
  const [msg, setMsg] = useState(null)
  const [camera, setCamera] = useState(false)
  const [saving, setSaving] = useState(false)
  const inputRef = useRef(null)

  useEffect(() => {
    if (!count.bin_tracking) return
    getAreas(count.location_id).then((rows) => setAreas(Array.isArray(rows) ? rows : [])).catch(() => setAreas([]))
  }, [count.bin_tracking, count.location_id])

  const add = async (raw) => {
    const c = String(raw || '').trim()
    if (!c || saving) return
    setCode('')
    if (count.bin_tracking) {
      const found = findArea(areas, c, count.location_id)
      if (found.area) {
        if (areas.some((a) => a.level === 'Bin') && found.area.level !== 'Bin') {
          setMsg({ tone: 'bad', text: `That’s ${found.area.level === 'Aisle' ? 'an' : 'a'} ${String(found.area.level).toLowerCase()} label (${found.area.path}). Scan a bin.` })
        } else {
          setBin(found.area)
          setMsg({ tone: 'good', text: `Now counting bin ${found.area.path}` })
        }
        return
      }
    }
    setSaving(true)
    try {
      const p = await resolveProductByCode(c).catch(() => null)
      if (!p?.product_id) { setMsg({ tone: 'bad', text: `No item has the code ${c}.` }); return }
      if (count.bin_tracking && !bin) { setMsg({ tone: 'warn', text: 'Scan the bin label first.' }); return }
      const res = await addCountScans(count.count_id, [{ scan_id: newScanId(), product_id: p.product_id, area_id: bin?.area_id ?? null, delta: 1 }])
      if (res?.rejected?.length) { setMsg({ tone: 'bad', text: `${p.name || p.sku}: ${res.rejected[0].error}` }); return }
      const line = (res?.lines || [])[0]
      setMsg({ tone: 'good', text: `${p.name || p.sku}: ${qty(line?.counted)} counted` })
      onAdded()
    } catch (e) {
      setMsg({ tone: 'bad', text: e.message || 'Could not add that.' })
    } finally {
      setSaving(false)
      inputRef.current?.focus()
    }
  }

  return (
    <section className="doc-card count-scan">
      <div className="doc-card-head">
        <h2>Scan here</h2>
        <span>{count.bin_tracking ? (bin ? `Bin ${bin.path}` : 'Scan a bin label first') : 'Each scan adds one'}</span>
      </div>
      <form className="bin-scan-row" onSubmit={(e) => { e.preventDefault(); add(code) }}>
        <ScanLine size={16} aria-hidden="true" />
        <input
          ref={inputRef}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder={count.bin_tracking ? 'Scan or type a bin label, then the items' : 'Scan or type a barcode or SKU'}
          aria-label="Scan or type a code"
          autoComplete="off"
        />
        <button type="submit" className="ci-btn ci-btn-ghost ci-btn-sm" disabled={saving || !code.trim()}>Add</button>
        <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={() => setCamera(true)}>Camera</button>
      </form>
      {msg && <p className={`count-scan-msg ${msg.tone}`} role="status">{msg.text}</p>}
      <BarcodeScanner open={camera} onClose={() => setCamera(false)} onScan={(c) => { setCamera(false); add(c) }} />
    </section>
  )
}

// Type how many were counted, or take a wrong scan out of the count.
function EditCount({ countId, line, onClose, onSaved }) {
  const [value, setValue] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  useEffect(() => {
    if (!line) return
    setValue(line.isNew ? '' : String(num(line.counted)))
    setError('')
  }, [line])

  const send = async (op) => {
    setSaving(true)
    setError('')
    try {
      const res = await addCountScans(countId, [{ scan_id: newScanId(), product_id: line.product_id, area_id: line.area_id ?? null, ...op }])
      if (res?.rejected?.length) { setError(res.rejected[0].error); return }
      onSaved()
    } catch (e) {
      setError(e.message || 'Could not save that.')
    } finally {
      setSaving(false)
    }
  }

  const save = () => {
    const n = Number(String(value).replace(',', '.'))
    if (String(value).trim() === '' || !Number.isFinite(n) || n < 0) { setError('Enter how many, or 0 if there are none.'); return }
    const delta = n - (line.isNew ? 0 : num(line.counted))
    if (!line.isNew && Math.abs(delta) < 1e-9) { onClose(); return }
    send({ delta })
  }

  return (
    <Modal
      open={!!line}
      onClose={onClose}
      title={line ? (line.name || line.sku) : ''}
      subtitle={line ? [line.sku, line.area_path].filter(Boolean).join(' · ') : ''}
      size="sm"
      footer={(
        <>
          {line && !line.isNew && <button type="button" className="ci-btn ci-btn-ghost" style={{ marginRight: 'auto' }} disabled={saving} onClick={() => send({ remove: true })}>Take out of the count</button>}
          <button type="button" className="ci-btn ci-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="ci-btn ci-btn-primary" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</button>
        </>
      )}
    >
      {error && <ErrorBanner message={error} />}
      <label className="ci-form-field">
        <span>How many were counted{line?.area_path ? ` in ${line.area_path}` : ''}? Enter 0 if there are none.</span>
        <input value={value} onChange={(e) => setValue(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') save() }} inputMode="decimal" autoFocus placeholder="0" />
      </label>
      {line && !line.isNew && <p className="doc-hint">Use “Take out of the count” for a wrong scan. Saving a new number keeps scans from other phones in the total.</p>}
    </Modal>
  )
}

function SendBack({ open, onClose, onSend }) {
  const [note, setNote] = useState('')
  useEffect(() => { if (open) setNote('') }, [open])
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Send back for recounting"
      subtitle="The scans stay, so people can fix what’s wrong and finish again."
      size="sm"
      footer={(
        <>
          <button type="button" className="ci-btn ci-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="ci-btn ci-btn-primary" onClick={() => onSend(note.trim())}>Send back</button>
        </>
      )}
    >
      <label className="ci-form-field">
        <span>What should they check? (optional)</span>
        <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} maxLength={255} placeholder="e.g. Recount the rice in aisle 3" />
      </label>
    </Modal>
  )
}
